import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { colors } from '../../../../theme/tokens';
import { ScopeType } from '../../../../api/accessGrantApi';

export interface EntityLabelProps {
  /** Entity scope — used for the copy control's accessible label. */
  scope: ScopeType;
  /** Human-readable name; shown primary. Falls back to `code` when absent/unresolved. */
  name?: string | null;
  /** Raw entity code (ten-… / cus-… / spo-…). Demoted but kept reachable + copyable. */
  code: string;
}

/**
 * Canonical label for a tenant / customer / sponsor entity — the SINGLE source
 * of truth for how such an entity is displayed. Both the tenant-access picker
 * options and the "Current access" grant chips render through this, so the
 * name-primary / code-demoted treatment stays consistent (no parallel label
 * implementations).
 *
 * Behaviour:
 *  - Name is primary (bold). Long names truncate with a full-name tooltip.
 *  - The raw code is demoted to a small monospace subline — never deleted, and
 *    kept reachable via a click-to-copy button (generous hit target). The click
 *    handler stops propagation so it works inside a clickable picker option
 *    without selecting that option.
 *  - Missing / unresolved name (e.g. a deleted tenant) → the code becomes the
 *    primary line so the row is never blank.
 */
const EntityLabel: React.FC<EntityLabelProps> = ({ scope, name, code }) => {
  const [copied, setCopied] = useState(false);
  const resolved = (name ?? '').trim();
  const hasName = resolved.length > 0;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      },
      () => {
        /* clipboard unavailable — no-op; the code is still visible above */
      },
    );
  };

  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Primary line: human name, or the code itself when no name resolves. */}
      <Tooltip title={hasName ? resolved : code} enterDelay={600} disableInteractive>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.35,
            color: colors.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {hasName ? resolved : code}
        </Typography>
      </Tooltip>

      {/* Demoted line: the raw code, kept reachable + copyable. Only shown when a
          name is already primary (otherwise the code is the primary line above). */}
      {hasName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, mt: 0.1 }}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontSize: 10.5,
              lineHeight: 1.3,
              color: colors.textTertiary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {code}
          </Typography>
          <Tooltip title={copied ? 'Copied' : 'Copy code'} disableInteractive>
            <IconButton
              onClick={handleCopy}
              size="small"
              aria-label={`Copy ${scope} code ${code}`}
              sx={{ p: 0.5, ml: 0.25, color: colors.textTertiary, '&:hover': { color: colors.textSecondary } }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 13, color: colors.successIcon }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 13 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default EntityLabel;
